/**
 * Login Page
 *
 * Authentication is delegated to the Identity service. This page renders a
 * minimal placeholder and redirects to identity/login on mount. The user is
 * sent back here (or the originally requested path) with the JWT in the URL
 * fragment, which authStore.initialize() consumes.
 */

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

export const LoginPage: React.FC = () => {
  const login = useAuthStore((state) => state.login);
  const sessionExpired = useAuthStore((state) => state.sessionExpired);
  const clearSessionExpiredFlag = useAuthStore((state) => state.clearSessionExpiredFlag);
  const location = useLocation();
  const [showSessionExpiredInfo, setShowSessionExpiredInfo] = useState(false);

  useEffect(() => {
    let expired = false;
    if (sessionExpired) {
      expired = true;
      clearSessionExpiredFlag();
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get('expired') === '1') {
      expired = true;
    }
    if (expired) {
      setShowSessionExpiredInfo(true);
      return;
    }

    const state = location.state as { from?: { pathname?: string; search?: string; hash?: string } } | undefined;
    const from = state?.from;
    let returnTo = window.location.origin + '/';
    if (from?.pathname) {
      returnTo = window.location.origin + from.pathname + (from.search ?? '') + (from.hash ?? '');
    }
    login(returnTo);
  }, [login, location.state, sessionExpired, clearSessionExpiredFlag]);

  const handleSignIn = () => {
    setShowSessionExpiredInfo(false);
    login(window.location.origin + '/');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1D407B] to-[#2B5A9E]">
      <div className="w-full max-w-md px-5">
        <div className="bg-white rounded-xl shadow-2xl p-10 text-center">
          <h1 className="text-2xl font-bold text-primary mb-2">Nova Control Center</h1>
          <p className="text-gray-600 text-sm mb-6">Redirecting you to sign in…</p>

          {showSessionExpiredInfo ? (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-left">
              <h3 className="font-semibold text-amber-900 mb-1">Session Expired</h3>
              <p className="text-sm text-amber-800 mb-3">
                Your login session has expired. Please sign in again to continue.
              </p>
              <button
                onClick={handleSignIn}
                className="px-4 py-2 bg-primary text-white font-semibold rounded-lg hover:bg-primary-light"
              >
                Sign in
              </button>
            </div>
          ) : (
            <button
              onClick={handleSignIn}
              className="px-4 py-2 bg-primary text-white font-semibold rounded-lg hover:bg-primary-light"
            >
              Continue
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
